import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchBscConfig,
  updateBscConfidence,
  fetchBscConfidencePreview,
} from "../../api/client";
import type { BscConfig, BscConfidencePreview } from "../../api/types";
import { useToast } from "../../context/ToastContext";

type TabId = "confidence" | "output" | "extraction" | "input";

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "confidence", label: "Confidence", icon: "tune" },
  { id: "output", label: "Output", icon: "output" },
  { id: "extraction", label: "Estrazione", icon: "auto_fix_high" },
  { id: "input", label: "Input", icon: "input" },
];

export function BscSettings() {
  const { addToast } = useToast();
  const [config, setConfig] = useState<BscConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("confidence");

  const load = useCallback(() => {
    setLoading(true);
    fetchBscConfig()
      .then((c) => { setConfig(c); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-12)" }}>
          <div className="spinner spinner-lg" />
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">
          <span className="material-symbols-outlined" style={{ verticalAlign: "middle", marginRight: 8 }}>error</span>
          Errore caricamento configurazione: {error || "Config non trovata"}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 className="page-title">
        <span className="material-symbols-outlined" style={{ fontSize: 28, verticalAlign: "middle", marginRight: 8 }}>tune</span>
        Configurazione BSC
      </h1>

      <div className="flow-tab-bar" style={{ display: "flex", gap: "var(--space-1)", borderBottom: "1px solid var(--indaco-gray-200)", marginBottom: "var(--space-4)" }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`flow-tab${activeTab === tab.id ? " active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "var(--space-2) var(--space-4)",
              border: "none", borderBottom: activeTab === tab.id ? "2px solid var(--indaco-brand-primary)" : "2px solid transparent",
              background: "transparent", cursor: "pointer",
              fontWeight: activeTab === tab.id ? "var(--font-weight-semibold)" : "var(--font-weight-medium)",
              color: activeTab === tab.id ? "var(--indaco-brand-primary)" : "var(--indaco-gray-600)",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "confidence" && (
        <ConfidenceTab config={config} onReload={load} addToast={addToast} />
      )}
      {activeTab === "output" && <OutputTab config={config} />}
      {activeTab === "extraction" && <ExtractionTab config={config} />}
      {activeTab === "input" && <InputTab config={config} />}
    </div>
  );
}

function ConfidenceTab({
  config, onReload, addToast,
}: {
  config: BscConfig;
  onReload: () => void;
  addToast: (type: "success" | "error" | "info", msg: string) => void;
}) {
  const [threshold, setThreshold] = useState(config.confidence_threshold_auto_approve);
  const [origThreshold, setOrigThreshold] = useState(config.confidence_threshold_auto_approve);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<BscConfidencePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dirty = threshold !== origThreshold;
  const thresholdDisplay = threshold >= 999 ? "Spento" : threshold.toFixed(2);

  useEffect(() => {
    if (threshold >= 999 || !dirty) {
      setPreview(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPreviewLoading(true);
      fetchBscConfidencePreview(threshold, 30)
        .then((p) => setPreview(p))
        .catch(() => setPreview(null))
        .finally(() => setPreviewLoading(false));
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [threshold, dirty]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateBscConfidence(threshold);
      setOrigThreshold(threshold);
      addToast("success", `Soglia auto-approvazione aggiornata: ${thresholdDisplay}`);
      onReload();
    } catch (e: unknown) {
      addToast("error", `Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setThreshold(origThreshold);
  };

  return (
    <div className="card" style={{ padding: "var(--space-5)" }}>
      <h3 style={{ margin: "0 0 var(--space-2)", fontSize: "var(--font-size-md)" }}>Soglia auto-approvazione</h3>
      <p className="text-sm text-muted" style={{ margin: "0 0 var(--space-4)" }}>
        Le pratiche con completeness ≥ soglia vengono approvate automaticamente senza intervento dell'operatore.
        Usa 999 (o "Spegni") per disattivare l'auto-approve e richiedere la revisione manuale di tutte le pratiche.
      </p>

      <div className="flow-field" style={{ marginBottom: "var(--space-4)" }}>
        <label className="flow-label" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "var(--space-2)" }}>
          Soglia: <strong>{thresholdDisplay}</strong>
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <span style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)" }}>0</span>
          <input
            type="range"
            className="flow-slider"
            min={0}
            max={1}
            step={0.01}
            value={threshold >= 999 ? 1 : threshold}
            onChange={(e) => setThreshold(parseFloat(e.target.value))}
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)" }}>1.0</span>
          <button
            className={`btn-toggle-sm${threshold >= 999 ? " active" : ""}`}
            onClick={() => setThreshold(threshold >= 999 ? 0.88 : 999)}
            title={threshold >= 999 ? "Attiva auto-approve" : "Spegni auto-approve"}
          >
            {threshold >= 999 ? "Attiva" : "Spegni"}
          </button>
        </div>
      </div>

      {threshold < 999 && dirty && (
        <div className="flow-preview-box" style={{
          background: "var(--indaco-gray-50)", borderRadius: "var(--border-radius-md)",
          padding: "var(--space-4)", marginTop: "var(--space-4)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-info)" }}>preview</span>
            <strong>Preview impatto</strong>
            {previewLoading && <div className="spinner spinner-sm" />}
          </div>
          {preview ? (
            <>
              <div style={{ fontSize: "var(--font-size-sm)", marginBottom: "var(--space-2)" }}>
                Su <strong>{preview.total}</strong> pratiche (ultimi {preview.period_days} giorni):
              </div>
              <div className="preview-bar-container" style={{ marginBottom: "var(--space-3)" }}>
                <div className="preview-bar preview-bar-auto" style={{
                  width: preview.total > 0 ? `${(preview.would_auto_approve / preview.total) * 100}%` : "0%",
                }} />
                <div className="preview-bar preview-bar-manual" style={{
                  width: preview.total > 0 ? `${(preview.would_need_review / preview.total) * 100}%` : "0%",
                }} />
              </div>
              <div style={{ display: "flex", gap: "var(--space-6)", fontSize: "var(--font-size-sm)" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="preview-dot preview-dot-auto" />
                  Auto-approvate: <strong>{preview.would_auto_approve}</strong>{" "}
                  ({preview.total > 0 ? Math.round((preview.would_auto_approve / preview.total) * 100) : 0}%)
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="preview-dot preview-dot-manual" />
                  Revisione manuale: <strong>{preview.would_need_review}</strong>
                </span>
              </div>
            </>
          ) : !previewLoading ? (
            <div className="text-sm text-muted">Nessun dato disponibile per la preview</div>
          ) : null}
        </div>
      )}

      <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-4)" }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
          {saving ? <><div className="spinner spinner-sm" style={{ marginRight: 6, display: "inline-block", verticalAlign: "middle" }} />Salvataggio…</> : "Salva"}
        </button>
        <button className="btn btn-outline" onClick={handleCancel} disabled={!dirty || saving}>
          Annulla
        </button>
      </div>
    </div>
  );
}

function OutputTab({ config }: { config: BscConfig }) {
  return (
    <div className="card" style={{ padding: "var(--space-5)" }}>
      <h3 style={{ margin: "0 0 var(--space-4)", fontSize: "var(--font-size-md)" }}>Configurazione output</h3>
      <div className="flow-readonly-card" style={{ marginBottom: "var(--space-3)", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>folder</span>
        <span>Directory output: <code>{config.output_path}</code></span>
      </div>
      <div className="flow-readonly-card" style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>description</span>
        <span>Formato: <strong>JSON</strong> (26 campi BSC piatti, un file per pratica, nominato <code>{"bsc_{bl_number}_{timestamp}.json"}</code>)</span>
      </div>
      <p className="text-sm text-muted" style={{ marginTop: "var(--space-4)" }}>
        Il JSON viene scritto al momento dell'approvazione. È consultabile e scaricabile dalla pagina "Coda Output".
      </p>
    </div>
  );
}

function ExtractionTab({ config }: { config: BscConfig }) {
  return (
    <div className="card" style={{ padding: "var(--space-5)" }}>
      <h3 style={{ margin: "0 0 var(--space-4)", fontSize: "var(--font-size-md)" }}>Configurazione estrazione</h3>

      <div className="flow-readonly-card" style={{ marginBottom: "var(--space-3)" }}>
        <div className="fw-semibold text-sm" style={{ marginBottom: "var(--space-2)" }}>Pipeline</div>
        <div className="text-sm">
          Le email con allegati BL + Invoice PDF vengono analizzate da <strong>Claude Sonnet</strong> che estrae
          in un'unica chiamata i 26 campi BSC (Bill of Lading + Fattura Commerciale). Estrazione testo PDF
          via <strong>pdfminer</strong> con fallback <strong>Claude Vision</strong> per PDF scansionati.
        </div>
      </div>

      <div className="flow-readonly-card" style={{ marginBottom: "var(--space-3)", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>warning</span>
        <span>Soglia warning completeness: <strong>{Math.round(config.completeness_warning_threshold * 100)}%</strong> (23/26 campi)</span>
      </div>

      <div className="flow-readonly-card">
        <div className="fw-semibold text-sm" style={{ marginBottom: "var(--space-2)" }}>Metrica completeness</div>
        <div className="text-sm text-muted">
          La completeness (0-1) è la frazione dei 26 campi BSC estratti correttamente.
          È usata come proxy di confidence per l'auto-approvazione e per i filtri nella dashboard.
        </div>
      </div>

      <p className="text-sm text-muted" style={{ marginTop: "var(--space-4)" }}>
        I prompt template di estrazione sono in <code>src/bsc/analyzer.py</code> e richiedono un deploy per essere modificati.
      </p>
    </div>
  );
}

function InputTab({ config }: { config: BscConfig }) {
  return (
    <div className="card" style={{ padding: "var(--space-5)" }}>
      <h3 style={{ margin: "0 0 var(--space-4)", fontSize: "var(--font-size-md)" }}>Configurazione input</h3>

      <div className="flow-readonly-card" style={{ marginBottom: "var(--space-3)", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>mail</span>
        <span>Mailbox: <code>frabemar_in@mail.infocert.it</code></span>
      </div>

      <div className="flow-readonly-card" style={{ marginBottom: "var(--space-3)", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>link</span>
        <span>Correlazione multi-email: <strong>{config.use_tag_correlation ? "Attiva" : "Disattivata"}</strong></span>
      </div>

      {config.use_tag_correlation && config.tag_extraction_pattern && (
        <div className="flow-readonly-card" style={{ marginBottom: "var(--space-3)", display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>code</span>
          <span>Pattern tag: <code>{config.tag_extraction_pattern}</code></span>
        </div>
      )}

      <p className="text-sm text-muted" style={{ marginTop: "var(--space-4)" }}>
        Il polling Graph API recupera le email dalla mailbox ogni ciclo batch. Le email con BL + Invoice
        vengono identificate dai filename (<code>_BL</code>, <code>_INV</code>) e raggruppate in pratiche.
      </p>
    </div>
  );
}
